import * as Sentry from '@sentry/browser';
import type { SagaIterator, Task } from 'redux-saga';
import { call, put, take, cancel, fork, spawn, delay } from 'redux-saga/effects';
import { select as typedSelect, call as typedCall } from 'typed-redux-saga';
import { selectUserId, isoNow, selectProfileOrganizationId } from 'flowhawk-core';
import type { SyncProfileInChatrooms } from 'flowhawk-core';

import parseProfile from '../Utils/profile';
import { isoNowUTC } from '../Utils/time';
import { MessageTypes } from '../Utils/types';
import type {
  AttachmentData,
  ChatroomInfo,
  ChatroomMemberMap,
  MessageId,
  PostAttachmentData,
  ChatroomMessage,
  ChatroomFile,
  ChatroomFileMap,
  Organization,
  ChatroomMemberInfo,
  MessageAcknowledgeMap,
  SearchResultMap,
  SearchResult,
  ChatroomMessageMap,
  MessageType,
  ReadStatusMap,
} from '../Utils/types';
import { NotificationSoundMap } from '../Themes/Sounds';
import { pushError, setSagaMessage } from '../Redux/ApplicationActions';
import { finishLoading, startLoading, finishLoadingEntity, startLoadingEntity } from '../Redux/LoadingActions';
import type {
  AddedToChatroom,
  ClearChatroomNotifications,
  RemovedFromChatroom,
  MarkMessagesRead,
  LoadChatMembers,
  LoadPinnedMessages,
  LoadMessageStatuses,
  AcknowledgeMessage,
  StartTyping,
  StopTyping,
  PinChatroom,
  UnpinChatroom,
  PinMessage,
  UnpinMessage,
  Search,
  OpenChatroom,
  LoadChatFiles,
  RedactMessage,
  UnredactMessage,
  EditMessage,
  DeleteFileMessage,
  AttemptReceiveNewMessage,
  PartialChatroomInfo,
  ReceiveChatroomMessagesInfo,
  BulkUpdateMessageStatus,
  LoadMessages,
  SetOffset,
  RefreshStaleMessages,
} from '../Redux/ChatListActions';
import {
  CHAT_PAGE_SIZE,
  loadMessages,
  setOffset,
  refreshStaleMessages,
  receiveChatroomInfo,
  receiveChatroomMembers,
  receiveBulkChatroomMembers,
  receiveBulkChatroomInfo,
  receiveChatroomMessages,
  bulkUpdateMessageStatus,
  setMessageStatus,
  updateChatroomInfo,
  receiveAcknowledge,
  closeChatroom,
  setSearchResults,
  loadChatMembers,
  receiveChatroomFiles,
  removeChatroom,
  receiveNewMessage,
  receivePinnedMessages,
  loadPinnedMessages,
  updateRoomIcon as updateRoomIconInList,
} from '../Redux/ChatListActions';
import {
  setMessageText,
  setMessageMedia,
  mediaUploadSuccess,
  setMentionedUsers,
  setMentionedNames,
  setMessageReply,
  type SubmitMessage,
} from '../Redux/ChatMessageActions';
import type { ChatSaveError, SaveChat } from '../Redux/ChatEditActions';
import {
  saveChatFailed,
  setInitialMessage,
  setMentionedPendingUsers,
  setMentionedPendingNames,
  receiveOrganizations,
  saveChatSuccess,
  resetChatEditState,
} from '../Redux/ChatEditActions';
import { addUsersToChatroom, addSingleUserToChatroom } from '../Redux/ChatroomActions';
import type { UpdateRoomIcon } from '../Redux/ChatroomActions';
import { getTeams } from '../Redux/TeamActions';
import api from '../Services/Api';
import { apiFetch } from './ApiSaga';
import { keys } from '../Utils/Object';
import createUuid from '../Utils/createUuid';
import { sendCommandAndGetResult, sendMessage, sendCommandNoResult } from './Socket';
import isString from '../Utils/isString';
import hasValue from '../Utils/hasValue';
import { singleErrorMessage } from '../Utils/flattenError';
import { parseMessageStatuses, parseEmptyMessageStatus } from '../Utils/parseMessageStatuses';
import type { GetApplicationData } from '../Redux/ApplicationActions';
import { getFeaturePermissionsSaga } from './PermissionSaga';
import { ENTITY_TYPE_CHATROOM } from '../Utils/hasPermissions';
import { receiveAvailableTags, incrementCaseUnreadCount, getFollowerGroup } from '../Redux/DonorActions';
import { convertToOrganization } from '../Utils/organizations';
import { refetchChatList } from '../Utils/hooks';
import type { RootState } from '../Redux';
import type { CommandResult } from './Socket/Commands';
import type {
  ApiChatroom,
  ApiGetInstantMessagesResponse,
  ApiInstantMessageSelectResponse,
  ApiGetMediaContentResponse,
  ApiInstantMessageStatusSelectResponse,
  ApiSearchInstantMessagesResponse,
  ApiSelectChatroomMembersResponse,
  ApiInstantMessage,
  ApiAcknowledgementMap,
  ApiUploadMessageMediaResponse,
  ApiGetPinnedMessagesResponse,
  ApiApplicationData,
} from '../Utils/apiTypes';
import { ResourceErrorInterface } from 'tg-resources';

export function* getApplicationDataSaga(action: GetApplicationData) {
  yield put(startLoading('applicationData'));

  yield call(getFeaturePermissionsSaga);
  yield put(getTeams());

  const requestData = {
    getOrganizations: action.getOrganizations,
    getChatrooms: action.getChatrooms,
    getChatroomMembers: action.getChatroomMembers,
    getInstantMessages: action.getInstantMessages,
    chatroomIds: action.chatroomIds,
  };
  const memberId = yield* typedSelect((state: RootState) => selectUserId(state.auth));
  const ack: CommandResult = yield sendCommandAndGetResult(undefined, memberId, 'get-application-data', [requestData]);

  if (!ack) {
    yield put(pushError('Failed to refresh data', undefined));
  } else if (ack.error) {
    yield put(pushError('Failed to refresh data', ack.error));
  } else if (ack.result) {
    const applicationData: ApiApplicationData = ack.result;

    const receivedRoomInfos: PartialChatroomInfo[] = [];
    const chatroomMemberInfoArray: ChatroomMemberInfo[] = [];
    const chatroomsMessagesArray: ReceiveChatroomMessagesInfo[] = [];

    // Push the organizations to state.
    if (Array.isArray(applicationData.organizations)) {
      const organizationsArray: Organization[] = [];

      const orgId = yield* typedSelect((state: RootState) => selectProfileOrganizationId(state.auth));

      for (let i = 0; i < applicationData.organizations.length; i += 1) {
        const organization = convertToOrganization(applicationData.organizations[i]);
        organizationsArray.push(organization);

        if (organization.id === orgId) {
          yield put(receiveAvailableTags(organization.availableTags));
        }
      }

      yield put(receiveOrganizations(organizationsArray));
    }

    // Iterate over all of the returned chatrooms, for each chatroom
    // Add the returned members and messages to the chatroom info object.
    if (Array.isArray(applicationData.chatrooms)) {
      applicationData.chatrooms.forEach((chatroom: ApiChatroom) => {
        const chatroomId = chatroom.chatroom_id;
        receivedRoomInfos.push({
          id: chatroom.chatroom_id,
          name: chatroom.chatroom_name,
          description: chatroom.chatroom_description,
          chatroomType: chatroom.chatroom_type,
          managed: chatroom.managed || false,
          creatorId: chatroom.creator_id,
          doNotDisturb: chatroom.do_not_disturb,
          pinNumber: chatroom.pin_number,
          memberCount: chatroom.member_count,
          managerIds: chatroom.manager_ids ?? [],
          totalMessages: chatroom.total_message_count,
          sentCount: chatroom.sent_count || 0,
          readCount: chatroom.read_count || 0,
          clearedCount: chatroom.cleared_count || 0,
          lastMessage: chatroom.last_instant_message_time
            ? {
                textContent: chatroom.last_instant_message_content,
                sentTime: chatroom.last_instant_message_time,
              }
            : undefined,
          lastUpdateTime: chatroom.last_update_time,
          lastActiveTime: chatroom.last_active_time || undefined,
          donorId: chatroom.donor_id,
          organId: chatroom.organ_id,
          organType: chatroom.organ_type,
          targetOrgId: chatroom.transplant_center,
          createDate: chatroom.create_date,
          donorClosed: chatroom.donor_closed,
          followerId: chatroom.follower_id,
          caseName: chatroom.case_name,
          followerName: chatroom.follower_name,
          roomIcon: chatroom.room_icon || undefined,
        });

        const memberMap: ChatroomMemberMap = {};
        const memberOrder: number[] = [];
        // Add chatroom members to the member map.
        if (applicationData.chatroom_members && Array.isArray(applicationData.chatroom_members[chatroomId])) {
          applicationData.chatroom_members[chatroomId].forEach((member) => {
            const profileData = parseProfile(member.user_information);

            memberMap[`${member.user_information.user_id}`] = {
              profile: profileData,
              membershipStatus: member.membership_status,
              doNotDisturb: hasValue(member.do_not_disturb) ? !!member.do_not_disturb : undefined,
              notificationSound: hasValue(member.alert_sound) ? member.alert_sound : undefined,
              priorityNotificationSound: hasValue(member.high_priority_alert_sound)
                ? member.high_priority_alert_sound
                : undefined,
              endDate: member.end_date,
              startDate: member.start_date,
              addedBy: member.added_by_id,
              removedBy: member.removed_by_id,
              addedByName: member.added_by_name,
              removedByName: member.removed_by_name,
            };

            memberOrder.push(member.user_information.user_id);
          });
        }

        if (memberOrder.length > 0) {
          chatroomMemberInfoArray.push({
            id: chatroomId,
            members: memberMap,
            memberOrder,
          });
        }

        // Iterate over the instant messages to create the instant message map for the chatroom
        if (applicationData.instant_messages && Array.isArray(applicationData.instant_messages[chatroomId])) {
          const messages: { [key: string]: ChatroomMessage } = {};
          const messageOrder: number[] = [];

          applicationData.instant_messages[chatroomId].forEach((message) => {
            const instantMessageId = message.instant_message_id;

            // Iterate over array of acknowledgements now to create the acknowledgment map.
            const ackMap: MessageAcknowledgeMap = {};
            if (
              instantMessageId &&
              applicationData.acknowledgements &&
              applicationData.acknowledgements[chatroomId] &&
              Array.isArray(applicationData.acknowledgements[chatroomId][instantMessageId])
            ) {
              applicationData.acknowledgements[chatroomId][instantMessageId].forEach((acknowledgement) => {
                ackMap[acknowledgement.chatroom_member_id] = {
                  chatId: acknowledgement.chatroom_id,
                  memberId: acknowledgement.chatroom_member_id,
                  messageId: acknowledgement.instant_message_id,
                  // Convert the symbol into hex by removing 'U+' and adding '0x'
                  symbol: `0x${acknowledgement.symbol.substring(2)}`,
                };
              });
            }
            if (instantMessageId) {
              // NOTE: type of instantMessageId is MessageId, which can techinically be number or string
              // ensure we have a number before using unshift
              const numberMessageId =
                typeof instantMessageId === 'number' ? instantMessageId : parseInt(instantMessageId, 10);
              messageOrder.unshift(numberMessageId);
              messages[instantMessageId] = {
                id: instantMessageId,
                senderId: message.sender_id,
                senderName: message.sender_name,
                sentTime: message.sent_time,
                messageType: message.chat_type_id,
                textContent: message.text_content || '',
                dataContent: message.data_content,
                fileName: message.file_name,
                presignedUrl: message.presigned_url,
                presignedThumbnailUrl: message.presigned_thumbnail_url,
                status: undefined,
                ack: ackMap,
                seenByCount: message.read_count || 0,
                seenByCurrentUser: message.is_seen_by_current_user || false,
                redactTime: message.redact_time,
                editTime: message.edit_time,
                editedBy: message.edited_by,
                replyingMessageId: message.replying_to,
                replyingTextContent: message.replying_to_text_content,
                replyingSenderId: message.replying_to_sender_id,
                parentMessageId: message.parent_message_id,
                highPriority: message.high_priority || false,
              };
            }
          });

          if (messageOrder.length > 0) {
            chatroomsMessagesArray.push({
              id: chatroomId,
              memberId,
              messages,
              messageOrder,
              hint: 'replace',
              lastMessage: {
                textContent: messages[`${messageOrder[messageOrder.length - 1]}`].textContent,
                sentTime: messages[`${messageOrder[messageOrder.length - 1]}`].sentTime,
              },
              lastActiveTime: chatroom.last_active_time || undefined,
              // This bulk bootstrap load is itself a full ('replace') reload with fresh presigned
              //  URLs, so stamp it the same as any other full refresh. Otherwise the room would
              //  look stale (messagesRefreshedTime unset) the moment you open it post-login, even
              //  though its data was just fetched.
              refreshedTime: isoNowUTC(),
              isOldPage: false,
            });
          }
        }
      });
    }

    if (receivedRoomInfos.length > 0) {
      // Store room info and then store the chat room order.
      yield put(receiveBulkChatroomInfo(receivedRoomInfos, true /* update chat order */));
    }

    // Dispatch the array of actions
    if (chatroomMemberInfoArray.length > 0) {
      yield put(receiveBulkChatroomMembers(memberId, chatroomMemberInfoArray));
    }

    for (let i = 0; i < chatroomsMessagesArray.length; i += 1) {
      const chatroomMessagesInfo = chatroomsMessagesArray[i];
      yield put(startLoadingEntity('chatMessages', chatroomMessagesInfo.id));
      yield put(
        receiveChatroomMessages(
          chatroomMessagesInfo.id,
          chatroomMessagesInfo.memberId,
          chatroomMessagesInfo.messages,
          chatroomMessagesInfo.messageOrder,
          chatroomMessagesInfo.hint,
          chatroomMessagesInfo.lastMessage,
          chatroomMessagesInfo.lastActiveTime,
          chatroomMessagesInfo.isOldPage,
          chatroomMessagesInfo.refreshedTime,
        ),
      );
      yield put(finishLoadingEntity('chatMessages', chatroomMessagesInfo.id));
    }
  }

  yield put(finishLoading('applicationData'));
}

// How long a room's cached messages (and their presigned media URLs) are trusted before
//  reopening the room forces a refresh instead of just fetching newer messages.
//  Must stay comfortably under the server's presigned media URL TTL (currently 1 hour)
//  so a reopen never lands in the gap where the cache is stale but not yet flagged as such.
const MESSAGES_REFRESH_INTERVAL_MS = 58 * 60 * 1000; // 58 minutes

// Upper bound on how many messages a stale-refresh will request in one call, even if the
//  user had paged back further than this via "Load More" before the room went stale.
const MAX_STALE_REFRESH_MESSAGE_COUNT = 250;

export function* openChatroomSaga(action: OpenChatroom): SagaIterator<void> {
  const { chatId } = action;

  const memberId = yield* typedSelect((state: RootState) => selectUserId(state.auth));
  const selectedResult = yield* typedSelect((state: RootState) => state.chatList.selectedResult);
  const loadedMessages = yield* typedSelect(
    (state: RootState) => ((state.chatList.chats[`${chatId}`] || {}).messageOrder || []).length,
  );
  const messagesRefreshedTime = yield* typedSelect(
    (state: RootState) => state.chatList.chats[`${chatId}`]?.messagesRefreshedTime,
  );
  const lastReconnectedTime = yield* typedSelect((state: RootState) => state.chatList.lastReconnectedTime);
  // Messages are stale if we have no refreshed time, the last refresh was more than
  // MESSAGES_REFRESH_INTERVAL_MS ago, or the socket has reconnected since the last refresh
  // (meaning updates to this room may have been missed while disconnected).
  const messagesAreStale =
    !messagesRefreshedTime ||
    Date.now() - new Date(messagesRefreshedTime).getTime() > MESSAGES_REFRESH_INTERVAL_MS ||
    (!!lastReconnectedTime && new Date(messagesRefreshedTime).getTime() < new Date(lastReconnectedTime).getTime());

  // Only load members if we don't have them yet
  const hadNoMembers = yield* typedSelect((state: RootState) => {
    const chat = state.chatList.chats[chatId];
    return !chat || !chat.memberOrder || chat.memberOrder.length === 0;
  });

  if (hadNoMembers) {
    yield put(loadChatMembers(chatId));
  }

  // Load pinned messages for this chatroom
  yield put(loadPinnedMessages(chatId));

  const messagesToLoad =
    selectedResult && selectedResult.messagePosition
      ? selectedResult.totalMessageCount - selectedResult.messagePosition + 1
      : 0;
  // If we have no messages and messagesToLoad < 50 then we load the first 50 messages.
  // If we have no messages and messagesToLoad > 50 then we load up to messagesToLoad (i.e., messagesToLoad === 63 we load 63 messages).
  if (loadedMessages === 0) {
    yield put(loadMessages(chatId, memberId, true, messagesToLoad));
  } else if (messagesAreStale) {
    // Cached messages (and their presigned media URLs) are stale. Refresh what's already loaded
    //  in one bounded call instead of the unread-backlog catch-up ChatList/LOAD_MESSAGES does for
    //  a true cold start, so a busy/neglected room can't balloon this into a huge fetch.
    yield put(refreshStaleMessages(chatId, memberId, messagesToLoad));
  } else {
    // If we already have messages and we need to load more then load the next 50.
    const newOffset = loadedMessages;
    const messageCountNeeded = messagesToLoad > newOffset ? messagesToLoad - newOffset : 0;
    yield put(setOffset(chatId, memberId, newOffset, messageCountNeeded));
  }
}

export function* searchChatSaga(action: Search): SagaIterator<void> {
  const { searchText } = action;

  yield put(startLoading('search'));

  // Debounce the task: https://github.com/redux-saga/redux-saga/blob/master/docs/recipes/README.md#debouncing
  yield delay(50);

  const { result, error } = yield* typedCall(
    apiFetch<ApiSearchInstantMessagesResponse>,
    api.txp.search,
    {},
    {
      text: searchText,
    },
  );

  if (error || !result) {
    yield put(finishLoading('search'));
  } else {
    const matchedMessages = result.matched_messages;

    const searchResults: SearchResultMap = {};

    const matchedMessagesKeys = keys(matchedMessages).map((key) => parseInt(key));

    for (let i = 0; i < matchedMessagesKeys.length; i += 1) {
      const messageInfoArray: SearchResult[] = [];
      const currentMessages = matchedMessages[matchedMessagesKeys[i]];

      for (let j = 0; j < currentMessages.length; j += 1) {
        const message = currentMessages[j].instant_message;
        messageInfoArray.push({
          instantMessage: {
            id: message.instant_message_id as MessageId,
            senderId: message.sender_id,
            sentTime: message.sent_time,
            messageType: message.chat_type_id,
            textContent: message.text_content || '',
            dataContent: message.data_content,
            displayName: message.display_name,
            fileName: message.file_name,
            presignedUrl: message.presigned_url,
            presignedThumbnailUrl: message.presigned_thumbnail_url,
            localPath: undefined,
            seenByCount: message.read_count || 0,
            seenByCurrentUser: message.is_seen_by_current_user || false,
            status: undefined,
            ack: {},
            senderName: message.sender_name,
            highPriority: message.high_priority,
          },
          messagePosition: currentMessages[j].message_position,
          chatroomId: currentMessages[j].chatroom_id,
          chatroomName: currentMessages[j].chatroom_name,
          totalMessageCount: currentMessages[j].total_message_count,
        });
      }
      searchResults[matchedMessagesKeys[i]] = messageInfoArray;
    }

    if (keys(searchResults).length === 0) {
      // null distinguishes a search that matched nothing from the initial {}, which
      // means no search has run. The room list shows no results only for the former.
      yield put(setSearchResults(null));
    } else {
      yield put(setSearchResults(searchResults));
    }

    yield put(finishLoading('search'));
  }
}

/**
 * Load chat members from the api
 *
 * @param {number} chatId
 * @param {boolean} returnData if true will return the members instead of triggering the redux action
 */
function* loadChatroomMembersWorkerSaga(chatId: number): SagaIterator<void> {
  yield put(startLoadingEntity('chatMembers', chatId));

  const { result, error } = yield* typedCall(apiFetch<ApiSelectChatroomMembersResponse>, api.txp.chatroomMembers, {
    chatId,
  });

  if (error || !result) {
    yield put(finishLoadingEntity('chatMembers', chatId));
    yield put(pushError('Failed to load room members', error));
  } else {
    const memberMap: ChatroomMemberMap = {};
    const memberOrder: number[] = [];

    const resultArr = result.chatroom_members || [];

    for (let i = 0; i < resultArr.length; i += 1) {
      const member = resultArr[i];

      const profileData = parseProfile(member.user_information);

      const memberData = {
        profile: profileData,
        membershipStatus: member.membership_status,
        startDate: member.start_date,
        endDate: member.end_date,
        doNotDisturb: member.do_not_disturb,
        notificationSound: member.alert_sound || NotificationSoundMap.default,
        priorityNotificationSound: member.high_priority_alert_sound || NotificationSoundMap.default,
        addedBy: member.added_by_id,
        removedBy: member.removed_by_id,
        addedByName: member.added_by_name,
        removedByName: member.removed_by_name,
      };

      memberOrder.push(member.user_information.user_id);
      memberMap[member.user_information.user_id] = memberData;
    }

    const memberId = yield* typedSelect((state: RootState) => selectUserId(state.auth));
    yield put(receiveChatroomMembers(chatId, memberId, memberMap, memberOrder, 'replace'));

    yield put(finishLoadingEntity('chatMembers', chatId));
  }
}

export function* loadChatMembersSaga(action: LoadChatMembers): SagaIterator<void> {
  // Debounce the task: https://github.com/redux-saga/redux-saga/blob/master/docs/recipes/README.md#debouncing
  yield delay(66);

  yield call(loadChatroomMembersWorkerSaga, action.chatId);
}

export function* loadPinnedMessagesSaga(action: LoadPinnedMessages): SagaIterator<void> {
  const { chatId } = action;

  yield put(startLoadingEntity('pinnedMessages', chatId));

  const { result, error } = yield* typedCall(apiFetch<ApiGetPinnedMessagesResponse>, api.txp.chatroomPinnedMessages, {
    chatId,
  });

  if (error || !result) {
    yield put(finishLoadingEntity('pinnedMessages', chatId));
    yield put(pushError('Failed to load bookmarked messages', error));
  } else {
    const pinnedMessagesMetadata = result.pinned_messages
      .sort((a: any, b: any) => a.pin_order - b.pin_order)
      .map((pm: any) => ({
        id: pm.instant_message_id,
        pinOrder: pm.pin_order,
        pinnedBy: pm.pinned_by,
        pinnedTime: pm.pinned_time,
        senderId: pm.instant_message?.sender_id || 0,
        senderName: pm.instant_message?.sender_name,
        textContent: pm.instant_message?.text_content,
        displayName: pm.instant_message?.display_name,
        fileName: pm.instant_message?.file_name,
        messageType: pm.instant_message?.chat_type_id || 0,
      }));

    yield put(receivePinnedMessages(chatId, pinnedMessagesMetadata));
    yield put(finishLoadingEntity('pinnedMessages', chatId));
  }
}

export function* loadChatFilesSaga(action: LoadChatFiles): SagaIterator<void> {
  const resArr: Array<ChatroomFile> = [];

  const { chatId, memberId } = action;

  yield put(startLoadingEntity('chatFiles', chatId));

  const { result, error } = yield* typedCall(apiFetch<ApiGetMediaContentResponse>, api.txp.chatroomMedia, {
    chatId,
  });

  if (error || !result || !result.media_content || !Array.isArray(result.media_content)) {
    yield put(setSagaMessage('Loading room files failed, try again later', '', ''));
    return;
  }
  const messages = yield* typedSelect((state: RootState) => state.chatList.chats[chatId]?.messages);
  const files = yield* typedSelect((state: RootState) => state.chatList.chats[chatId]?.files);

  result.media_content.forEach((fileData) => {
    // Preserve any in-flight upload blob URL the message store may already hold
    // for this file so the just-uploaded preview doesn't briefly disappear.
    const localPath =
      messages?.[`${fileData.instant_message_id}`]?.localPath ||
      files?.[`${fileData.instant_message_id}`]?.localPath ||
      undefined;

    resArr.push({
      id: fileData.instant_message_id,
      fileName: fileData.file_name,
      displayName: fileData.display_name || fileData.file_name,
      localPath,
      presignedUrl: fileData.presigned_url,
      presignedThumbnailUrl: fileData.presigned_thumbnail_url,
      messageType: fileData.chat_type_id,
      memberId: fileData.user_id,
      sentTime: fileData.date_uploaded,
      size: fileData.file_size,
    });
  });

  const fileMap: ChatroomFileMap = {};
  const allFiles: Array<MessageId> = [];
  const myFiles: Array<MessageId> = [];

  (resArr || []).forEach((file) => {
    fileMap[`${file.id}`] = file;
    allFiles.push(file.id);

    if (file.memberId === memberId) {
      myFiles.push(file.id);
    }
  });

  yield put(receiveChatroomFiles(chatId, fileMap, allFiles, myFiles));

  yield put(finishLoadingEntity('chatFiles', chatId));
}

export function* clearChatroomNotificationsSaga(action: ClearChatroomNotifications) {
  const { chatId } = action;

  const memberId = yield* typedSelect((state: RootState) => selectUserId(state.auth));
  yield sendCommandNoResult(chatId, memberId, 'clear-notifications', [chatId]);
}

export function* handleAddedToChatroomSaga(action: AddedToChatroom): SagaIterator<void> {
  const { roomId } = action;

  yield put(startLoading('chatrooms'));

  // sent_count, read_count, and cleared_count are 0, since user has just been added to chatroom
  yield put(
    receiveChatroomInfo(
      action.roomId,
      action.name || 'a room',
      action.description || '',
      action.chatroomType,
      action.managed,
      action.creatorId,
      action.memberCount,
      undefined,
      action.totalMessages || 0,
      0,
      0,
      0,
      action.lastMessage,
      action.lastUpdateTime,
      action.lastActiveTime,
      action.donorId,
      action.organId,
      undefined,
      action.followerId,
      action.createDate,
      action.caseName,
      action.followerName,
    ),
  );

  const roomName = yield* typedSelect(
    (state: RootState) => state.chatList.chats[`${roomId}`] && state.chatList.chats[`${roomId}`].name,
  );
  yield put(setSagaMessage('Added to room', roomName, ''));

  yield put(finishLoading('chatrooms'));

  const openCaseId = yield* typedSelect((state: RootState) => state.donor.openDonorId);

  // load new follower group data if the user has that case open
  // to avoid showing stale member data
  // NOTE: initEditState is false - this is a background cache refresh, not the user opening the
  // group to edit it, so it must not stomp on followerEdit (see joinFollowerGroupSaga).
  if (!action.isPushNotification && action.followerId && action.donorId && openCaseId === action.donorId) {
    yield put(getFollowerGroup(action.donorId, action.followerId, false));
  }
}

export function* handleRemovedFromChatroomSaga(action: RemovedFromChatroom): SagaIterator<void> {
  // Debounce the task: https://github.com/redux-saga/redux-saga/blob/master/docs/recipes/README.md#debouncing
  yield delay(400);

  const { roomId } = action;

  // Notify the user about the removal
  const room = yield* typedSelect((state: RootState) => state.chatList.chats[`${roomId}`]);
  const roomName = room?.name;
  const openCaseId = yield* typedSelect((state: RootState) => state.donor.openDonorId);
  yield put(pushError(`You've been removed from room ${roomName ? ' ' : ''}${roomName}`, undefined, ''));
  yield put(setSagaMessage('Removed from room', roomName, ''));

  yield put(setMessageText(roomId, ''));
  yield put(setMessageMedia(roomId, []));
  yield put(setMentionedUsers(roomId, []));
  yield put(setMentionedNames(roomId, []));
  yield put(closeChatroom(roomId));

  // Remove chatroom from redux
  yield put(removeChatroom(roomId));

  // load new follower group data if the user has that case open
  // to avoid showing stale member data
  if (room?.followerId && room?.donorId && openCaseId === room?.donorId) {
    yield put(getFollowerGroup(room.donorId, room.followerId, false));
  }
}

function* loadMessageStatusesWorkerSaga(
  chatId: number,
  memberId: number,
  messageIds: Array<number>,
): SagaIterator<void> {
  const messageIdList = JSON.stringify(messageIds);

  const { result, error } = yield* typedCall(
    apiFetch<ApiInstantMessageStatusSelectResponse>,
    api.txp.chatroomMessageStatuses,
    {
      chatId,
      messageIdList,
    },
  );

  if (error || !result) {
    yield put(pushError('Failed to load message statuses for room', error));
    return;
  }

  let statusesCommit: BulkUpdateMessageStatus | undefined;

  if (result.instant_message_status_list.length === 0) {
    // no one has read the message yet
    statusesCommit = parseEmptyMessageStatus(memberId, chatId, messageIds[0], true);
  } else {
    statusesCommit = parseMessageStatuses(memberId, chatId, result, true);
  }

  if (statusesCommit) {
    yield put(statusesCommit);
  }
}

export function* loadMessageStatusesSaga(action: LoadMessageStatuses): SagaIterator<void> {
  const { chatId, memberId, messageIds } = action;

  // No need to try to load if no ids
  if (messageIds.length === 0) {
    return;
  }

  for (let i = 0; i < messageIds.length; i += 1) {
    yield put(startLoadingEntity('chatMessageStatuses', `${chatId}-${messageIds[i]}`));
  }

  // Debounce the task: https://github.com/redux-saga/redux-saga/blob/master/docs/recipes/README.md#debouncing
  yield delay(50);
  yield call(loadMessageStatusesWorkerSaga, chatId, memberId, messageIds);

  for (let i = 0; i < messageIds.length; i += 1) {
    yield put(finishLoadingEntity('chatMessageStatuses', `${chatId}-${messageIds[i]}`));
  }
}

export function* loadChatroomMessages(
  chatId: number,
  memberId: number,
  limit: number,
  offset: number = 0,
  resetMessages: boolean,
): SagaIterator<void> {
  yield put(startLoadingEntity('chatMessages', chatId));

  // Debounce the task: https://github.com/redux-saga/redux-saga/blob/master/docs/recipes/README.md#debouncing
  yield delay(50);

  let theLimit = limit;
  let theOffset = offset;

  // If we are loading messages in a room that has more unread messages than the limit
  //  lets load all of them at once
  if (offset !== 0 && resetMessages) {
    theLimit += theOffset;
    theOffset = 0;
  }

  const { result, error } = yield* typedCall(
    apiFetch<ApiGetInstantMessagesResponse>,
    api.txp.chatroomMessages,
    {
      chatId,
      limit: theLimit,
      offset: theOffset,
    },
    {
      sort_order: 'Descending',
    },
  );

  if (error || !result) {
    yield put(pushError('Loading room messages failed, try again later', error));
  } else {
    const messages: ChatroomMessageMap = {};
    const messageOrder = [];
    const rMsg: ApiInstantMessageSelectResponse = result.messages || {};

    // $FlowFixMe: https://github.com/facebook/flow/issues/4328
    const resultArr: ApiInstantMessage[] = rMsg.instant_messages || [];

    const acknowledgeObj: ApiAcknowledgementMap = rMsg.acknowledgements;
    const chat = yield* typedSelect((state: RootState) => state.chatList.chats[chatId]);
    const files = chat ? chat.files : {};

    // The messages come in in reverse order (newest first). This is good for pagination
    //  since we can only load the newest messages first. However we need to store the items in the order
    //  we display them (hence the reverse iteration)
    for (let i = resultArr.length - 1; i >= 0; i -= 1) {
      const message = resultArr[i];

      const messageAcks: MessageAcknowledgeMap = {};
      const acknowledgeObjKeys = keys(acknowledgeObj);
      for (let j = 0; j < acknowledgeObjKeys.length; j += 1) {
        const currentAckKey = acknowledgeObjKeys[j];
        if (parseInt(currentAckKey, 10) === message.instant_message_id) {
          const ackKey = parseInt(currentAckKey, 10);
          for (let k = 0; k < acknowledgeObj[ackKey].length; k += 1) {
            messageAcks[acknowledgeObj[ackKey][k].chatroom_member_id] = {
              chatId: acknowledgeObj[ackKey][k].chatroom_id,
              messageId: acknowledgeObj[ackKey][k].instant_message_id,
              memberId: acknowledgeObj[ackKey][k].chatroom_member_id,
              // remove U+ and replace with 0x to display emoji
              symbol: `0x${acknowledgeObj[ackKey][k].symbol.substring(2)}`,
            };
          }
        }
      }

      // Carry forward any in-flight upload blob URL the chat-files store may
      // already hold for this message, so a just-uploaded preview doesn't
      // briefly disappear while server data lands.
      const fileLocalPath =
        files && files[`${message.instant_message_id}`] ? files[`${message.instant_message_id}`].localPath : undefined;

      if (message.instant_message_id) {
        messageOrder.push(message.instant_message_id);
        messages[message.instant_message_id] = {
          id: message.instant_message_id,
          senderId: message.sender_id,
          senderName: message.sender_name,
          sentTime: message.sent_time,
          messageType: message.chat_type_id,
          textContent: message.text_content || '',
          dataContent: message.data_content ? message.data_content : null,
          displayName: message.display_name,
          fileName: message.file_name,
          presignedUrl: message.presigned_url,
          presignedThumbnailUrl: message.presigned_thumbnail_url,
          localPath: fileLocalPath,
          status: undefined,
          seenByCount: message.read_count || 0,
          seenByCurrentUser: message.is_seen_by_current_user || false,
          ack: messageAcks,
          redactTime: message.redact_time,
          editTime: message.edit_time,
          editedBy: message.edited_by,
          replyingMessageId: message.replying_to,
          replyingTextContent: message.replying_to_text_content,
          replyingMessageType: message.replying_to_chat_type_id,
          replyingDataContent: message.replying_to_data_content,
          replyingFilePath: message.replying_to_file_path,
          replyingSenderId: message.replying_to_sender_id,
          parentMessageId: message.parent_message_id,
          highPriority: message.high_priority || false,
        };
      }
    }

    let hint: 'end' | 'replace' | 'start' = 'end';

    if (resetMessages) {
      hint = 'replace';
    } else if (offset !== 0) {
      // Older messages should be pushed to the beginning of the array
      hint = 'start';
    }

    const lastActiveTime = chat ? chat.lastActiveTime : undefined;
    const refreshedTime = resetMessages ? isoNowUTC() : undefined;

    yield put(
      receiveChatroomMessages(
        chatId,
        memberId,
        messages,
        messageOrder,
        hint,
        undefined,
        lastActiveTime,
        offset !== 0,
        refreshedTime,
      ),
    );
  }

  yield put(finishLoadingEntity('chatMessages', chatId));
}

function isLoadMessagesAction(action: LoadMessages | SetOffset | RefreshStaleMessages): action is LoadMessages {
  return 'resetMessages' in action && typeof action.resetMessages === 'boolean';
}

function isSetOffsetAction(action: LoadMessages | SetOffset | RefreshStaleMessages): action is SetOffset {
  return 'offset' in action && typeof action.offset === 'number';
}

export function* loadChatroomMessagesManager(): SagaIterator<void> {
  const tasks: { [key: number]: Task } = {};

  while (true) {
    const action: LoadMessages | SetOffset | RefreshStaleMessages = yield take([
      'ChatList/LOAD_MESSAGES',
      'ChatList/SET_PAGE_OFFSET',
      'ChatList/REFRESH_STALE_MESSAGES',
    ]);

    const { chatId, memberId } = action;
    // ChatList/REFRESH_STALE_MESSAGES always does a full 'replace' reload, same as a genuine
    //  ChatList/LOAD_MESSAGES cold start — the difference is in how `offset`/`limit` get sized
    //  below, not whether the fetch result replaces the store.
    const resetMessages = isLoadMessagesAction(action)
      ? action.resetMessages
      : action.type === 'ChatList/REFRESH_STALE_MESSAGES';
    let offset = isSetOffsetAction(action) ? action.offset : 0;
    let limit =
      action.numberMessages && action.numberMessages > CHAT_PAGE_SIZE ? action.numberMessages : CHAT_PAGE_SIZE;

    let shouldLoad = true;

    if (action.type === 'ChatList/LOAD_MESSAGES') {
      offset = yield* typedSelect((state: RootState) => (state.chatList.chats[`${chatId}`] || {}).offset || 0);
    } else if (action.type === 'ChatList/SET_PAGE_OFFSET') {
      // Get current amount of messages we have locally
      const messagesCount = yield* typedSelect(
        (state: RootState) => ((state.chatList.chats[`${chatId}`] || {}).messageOrder || []).length,
      );

      // If we already have enough messages locally, skip loading more of them
      if (offset + limit < messagesCount) {
        shouldLoad = false;
      }
    } else if (action.type === 'ChatList/REFRESH_STALE_MESSAGES') {
      // `offset` is deliberately left at 0 here (unlike ChatList/LOAD_MESSAGES, which looks up
      //  the room's persisted unread-backlog offset) — we only want to refresh the most recent
      //  window of messages, not re-trigger an unread-backlog catch-up on every stale reopen.
      // Refresh at least as much as the user already has loaded (so pages they paged back
      //  through via "Load More" don't just vanish), capped so a room nobody has closed in a
      //  long time can't turn this into an unbounded fetch.
      const currentlyLoadedCount = yield* typedSelect(
        (state: RootState) => state.chatList.chats[`${chatId}`]?.messageOrder?.length || 0,
      );
      limit = Math.min(Math.max(limit, currentlyLoadedCount), MAX_STALE_REFRESH_MESSAGE_COUNT);
    }
    if (shouldLoad) {
      if (tasks[chatId]) {
        yield cancel(tasks[chatId]);

        delete tasks[chatId];
      }

      tasks[chatId] = yield fork(loadChatroomMessages, chatId, memberId, limit, offset, resetMessages);
    }
  }
}

function createTemporaryMessage(
  memberId: number,
  attachment?: AttachmentData,
): { messageType: MessageType; tmpId: string } {
  // Use a temporary id for the message and update it with response from server
  const uuid = createUuid(memberId);
  let messageType: MessageType = MessageTypes.text;

  if (attachment) {
    switch (attachment.mime) {
      case 'image/jpeg':
        messageType = MessageTypes.jpeg;
        break;

      case 'image/jpg':
        messageType = MessageTypes.jpg;
        break;

      case 'image/png':
        messageType = MessageTypes.png;
        break;

      case 'image/gif':
        messageType = MessageTypes.gif;
        break;

      case 'video/mp4':
        messageType = MessageTypes.mp4;
        break;

      case 'video/quicktime':
        messageType = MessageTypes.mov;
        break;

      case 'application/pdf':
        messageType = MessageTypes.pdf;
        break;

      case 'application/msword':
        messageType = MessageTypes.doc;
        break;

      case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
        messageType = MessageTypes.docx;
        break;

      case 'text/csv':
        messageType = MessageTypes.csv;
        break;

      case 'application/vnd.ms-excel':
        messageType = MessageTypes.xls;
        break;

      case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
        messageType = MessageTypes.xlsx;
        break;

      default:
        // Uploading file with unknown mime will fail, so set message type to 'unknown' so message sending will also fail
        messageType = MessageTypes.unknown;
    }
  }

  return {
    messageType,
    tmpId: uuid,
  };
}

function* addChatMessage(
  chatId: number,
  memberId: number,
  textContent: string,
  attachments: Array<AttachmentData>,
  mentionedUsers: Array<number>,
  replyMessageId?: number,
  highPriority?: boolean,
) {
  // Get the caseId for this room to send to server for unread case counts
  const chatRoom = yield* typedSelect((state: RootState) => state.chatList.chats[`${chatId}`]);
  const caseId = chatRoom ? chatRoom.donorId : undefined;
  if (attachments && attachments.length > 0) {
    const token = yield* typedSelect((state: RootState) => state.auth.accessToken);
    const postAttachment: Array<PostAttachmentData> = [];
    let tmpData = { messageType: -1, tmpId: '-1' };
    try {
      for (let i = 0; i < attachments.length; i += 1) {
        yield put(startLoading('media'));
        tmpData = yield typedCall(createTemporaryMessage, memberId, attachments[i]);

        const root = (import.meta.env.VITE_API_ROOT || '').replace(/\/$/, '');

        const encodedName = encodeURIComponent(attachments[i].fileName);
        const apiPath = `${root}/chatrooms/${chatId}/messages/media/url/${encodedName}/${attachments[i].mime}`;

        const form = new FormData();
        form.append('message', attachments[i].file);
        const headers = {
          Apikey: import.meta.env.VITE_API_KEY || '',
          Authorization: `Bearer ${token}`,
        };

        // Upload attachment
        const result: ApiUploadMessageMediaResponse = yield fetch(apiPath, {
          method: 'POST',
          headers,
          body: form,
        }).then((response) => response.json());

        // Create post attachment data using response from server
        postAttachment.push({
          file_name: result.media_content_path,
          display_name: attachments[i].displayName,
          file_size: attachments[i].size,
          chat_type_id: tmpData.messageType,
        });
      }

      // Send the message via socket
      yield fork(
        sendMessage,
        chatId,
        memberId,
        textContent,
        MessageTypes.multipartMedia,
        tmpData.tmpId,
        postAttachment,
        mentionedUsers,
        replyMessageId,
        caseId,
        highPriority,
      );
      yield put(finishLoading('media'));
    } catch (error) {
      const newError = error instanceof ResourceErrorInterface ? error : new Error('Unknown media upload error');
      yield put(pushError('Failed to upload media', newError));
      yield put(setMessageStatus(chatId, tmpData.tmpId, 'failed'));
      yield put(finishLoading('media'));
    }
  } else {
    const tmpData = yield* typedCall(createTemporaryMessage, memberId);

    // Send message via socket
    yield fork(
      sendMessage,
      chatId,
      memberId,
      textContent,
      tmpData.messageType,
      tmpData.tmpId,
      [],
      mentionedUsers,
      replyMessageId,
      caseId,
      highPriority,
    );
  }
  // Clear the ChatInput
  yield put(mediaUploadSuccess(chatId));
  yield put(setMessageText(chatId, ''));
  yield put(setMessageMedia(chatId, []));
  yield put(setMessageReply(chatId, undefined));
  yield put(setMentionedUsers(chatId, []));
  yield put(setMentionedNames(chatId, []));
  yield put(setInitialMessage('', []));
  yield put(setMentionedPendingUsers([]));
  yield put(setMentionedPendingNames([]));
}

export function* addChatMessageWatcher(): SagaIterator<void> {
  while (true) {
    const { chatId, highPriority }: SubmitMessage = yield take('ChatMessage/SUBMIT');
    const memberId = yield* typedSelect((state: RootState) => selectUserId(state.auth));
    const message = yield* typedSelect((state: RootState) => state.chatMessage.messages[`${chatId}`]);

    // Ignore empty messages
    if (message && (message.text || (message.attachments && message.attachments.length > 0))) {
      const mentionedUsers = message.mentionedUsers ? message.mentionedUsers : [];
      yield call(
        addChatMessage,
        chatId,
        memberId,
        message.text,
        message.attachments,
        mentionedUsers,
        message.replyMessageId,
        highPriority,
      );
    }
  }
}

// Sends the read-messages command and applies the resulting statuses locally.
//  Split out of markMessagesReadSaga so it can be spawned: sendCommandAndGetResult puts the command
//  on the socket before it starts waiting on the ack, so once we are here the server will mark the
//  messages read whether or not this task survives. Cancelling at that point would not undo the
//  read, it would only discard the local status update and leave the room's badge stuck showing
//  unread messages the server already considers read.
function* sendReadMessagesCommand(action: MarkMessagesRead, items: MessageId[]): SagaIterator<void> {
  try {
    const memberId = yield* typedSelect((state: RootState) => selectUserId(state.auth));
    const ack: CommandResult | undefined = yield call(
      sendCommandAndGetResult,
      action.chatId,
      memberId,
      'read-messages',
      [items],
    );

    if (ack && ack.result) {
      const statuses: { [key: string]: ReadStatusMap } = { [`${action.chatId}`]: {} };

      for (let i = 0; i < ack.result.instant_message_status_list.length; i += 1) {
        statuses[`${action.chatId}`][`${ack.result.instant_message_status_list[i].instant_message_id}`] = {
          [action.memberId]: isoNow(),
        };
      }

      yield put(
        bulkUpdateMessageStatus(
          action.chatId,
          action.memberId,
          statuses,
          'extend',
          ack.result.new_status_count,
          ack.result.updated_status_count,
        ),
      );
    } else {
      yield put(pushError('Failed to update read messages status', ack?.error));
    }
  } catch (err) {
    // A spawned task is detached from its parent, so an uncaught error here would propagate to the
    //  root saga and tear down the whole tree. Report it and leave the room to retry instead.
    Sentry.captureException(err, {
      extra: { errorMsg: 'sendReadMessagesCommand Failed', chatId: action.chatId, untilId: action.untilId },
    });
  }
}

export function* markMessagesReadSaga(action: MarkMessagesRead) {
  // many markReadMessages can get get fired in quick succession when
  // rapidly scrolling through new messages. Wait a bit before processing
  // (with takeLatest to cancel previous calls) to avoid unnecessary API calls.
  // Nothing has been sent yet during this delay, so cancelling here is correct:
  yield delay(1000);

  const chatInfo: ChatroomInfo = yield* typedSelect((state: RootState) => state.chatList.chats[action.chatId]);

  const isValidMessageToRead = (messageId: MessageId) => {
    // Do not mark messages that do not exist locally (or are temporary)
    if (!messageId || !chatInfo.messages[`${messageId}`] || isString(messageId)) {
      return false;
    }

    // Do not mark own messages
    if (chatInfo.messages[`${messageId}`].senderId === action.memberId) {
      return false;
    }

    // Only mark messages not already marked
    return !chatInfo.messages[`${messageId}`].seenByCurrentUser;
  };

  const { untilId } = action;
  const items: MessageId[] = [];

  // Mark all previous messages as well
  // Check that chatInfo and messageOrder exist
  if (chatInfo && chatInfo.messageOrder) {
    const markUntilIndex = chatInfo.messageOrder.indexOf(untilId);

    if (markUntilIndex === -1) {
      // untilId isn't in this room's message order (e.g. a stale/temporary id, or ordering has
      //  diverged from what was displayed). Report that an invalid ID was provided.
      Sentry.captureException(new Error('markMessagesReadSaga: untilId not found in messageOrder'), {
        extra: { errorMsg: 'markMessagesReadSaga Failed', chatId: action.chatId, untilId },
      });

      return;
    }

    for (let i = markUntilIndex; i >= 0; i -= 1) {
      const messageId = chatInfo.messageOrder[i];

      if (isValidMessageToRead(messageId)) {
        items.push(messageId);
      }
    }
  }

  // Send the commands if needed. Detached from this task so that a takeLatest cancellation (the
  //  user opening another room) can no longer strand a command that is already on its way out.
  if (items && items.length > 0) {
    yield spawn(sendReadMessagesCommand, action, items);
  }
}

export function* acknowledgeMessageSaga(action: AcknowledgeMessage) {
  const { chatId, messageId, symbol } = action;

  // Ensure takeLatest has time to handle doubleclicks
  // https://github.com/redux-saga/redux-saga/blob/master/docs/recipes/README.md#debouncing
  yield delay(50);

  const memberId = yield* typedSelect((state: RootState) => selectUserId(state.auth));
  const ack: CommandResult = yield sendCommandAndGetResult(chatId, memberId, 'ack-message', [messageId, symbol]);

  if (!ack || (ack && ack.error)) {
    yield put(pushError('Failed to send acknowledgment', ack.error));
  } else {
    yield put(receiveAcknowledge(chatId, messageId, memberId, `0x${symbol.substring(2)}`));
  }
}

export function* redactMessageSaga(action: RedactMessage) {
  const { chatId, messageId } = action;

  // Ensure takeLatest has time to handle doubleclicks
  // https://github.com/redux-saga/redux-saga/blob/master/docs/recipes/README.md#debouncing
  yield delay(50);

  const memberId = yield* typedSelect((state: RootState) => selectUserId(state.auth));
  yield sendCommandNoResult(chatId, memberId, 'redact-message', [messageId]);
}

export function* unredactMessageSaga(action: UnredactMessage) {
  const { chatId, messageId } = action;

  // Ensure takeLatest has time to handle doubleclicks
  // https://github.com/redux-saga/redux-saga/blob/master/docs/recipes/README.md#debouncing
  yield delay(50);

  const memberId = yield* typedSelect((state: RootState) => selectUserId(state.auth));
  yield sendCommandNoResult(chatId, memberId, 'unredact-message', [messageId]);
}

export function* editMessageSaga(action: EditMessage) {
  const { chatId, messageId, textContent } = action;

  // Ensure takeLatest has time to handle doubleclicks
  // https://github.com/redux-saga/redux-saga/blob/master/docs/recipes/README.md#debouncing
  yield delay(50);

  const memberId = yield* typedSelect((state: RootState) => selectUserId(state.auth));
  yield sendCommandNoResult(chatId, memberId, 'edit-message', [messageId, textContent]);
}

export function* deleteFileMessageSaga(action: DeleteFileMessage) {
  const { chatId, messageId } = action;

  // Ensure takeLatest has time to handle doubleclicks
  yield delay(50);

  const memberId = yield* typedSelect((state: RootState) => selectUserId(state.auth));
  yield sendCommandNoResult(chatId, memberId, 'delete-file-message', [messageId]);
}

export function* startTypingSaga(action: StartTyping) {
  const { chatId } = action;

  const memberId = yield* typedSelect((state: RootState) => selectUserId(state.auth));
  const ack: CommandResult = yield sendCommandAndGetResult(chatId, memberId, 'start-typing', [chatId]);

  if (!ack || (ack && ack.error)) {
    yield put(pushError('Failed to send start-typing signal', ack.error));
  }
}

export function* stopTypingSaga(action: StopTyping) {
  const { chatId } = action;

  const memberId = yield* typedSelect((state: RootState) => selectUserId(state.auth));
  const ack: CommandResult = yield sendCommandAndGetResult(chatId, memberId, 'stop-typing', [chatId]);

  if (!ack || (ack && ack.error)) {
    yield put(pushError('Failed to send stop-typing signal', ack.error));
  }
}

export function* pinChatroomSaga(action: PinChatroom) {
  const { chatId } = action;

  const memberId = yield* typedSelect((state: RootState) => selectUserId(state.auth));
  yield sendCommandNoResult(chatId, memberId, 'pin-chatroom', [chatId]);
}

export function* unpinChatroomSaga(action: UnpinChatroom) {
  const { chatId } = action;

  const memberId = yield* typedSelect((state: RootState) => selectUserId(state.auth));
  yield sendCommandNoResult(chatId, memberId, 'unpin-chatroom', [chatId]);
}

export function* pinMessageSaga(action: PinMessage) {
  const { chatId, messageId } = action;

  const memberId = yield* typedSelect((state: RootState) => selectUserId(state.auth));
  yield sendCommandNoResult(chatId, memberId, 'pin-message', [messageId]);
}

export function* unpinMessageSaga(action: UnpinMessage) {
  const { chatId, messageId } = action;

  const memberId = yield* typedSelect((state: RootState) => selectUserId(state.auth));
  yield sendCommandNoResult(chatId, memberId, 'unpin-message', [messageId]);
}

export function* updateChatroomData(chatId: number, description: string, name: string, managed: boolean) {
  const oldRoomData = yield* typedSelect((state: RootState) => state.chatList.chats[`${chatId}`] || {});

  if (name.length === 0) {
    const errors: ChatSaveError = {};

    errors.name = 'Room name cannot be empty.';

    yield put(saveChatFailed(errors));

    return;
  }

  yield put(updateChatroomInfo(chatId, name, description, managed, isoNow()));

  const memberId = yield* typedSelect((state: RootState) => selectUserId(state.auth));
  const ack: CommandResult = yield sendCommandAndGetResult(chatId, memberId, 'update-chatroom', [
    description,
    name,
    managed,
  ]);

  if (!ack || (ack && ack.error)) {
    // revert the name locally if it failed
    yield put(
      updateChatroomInfo(
        chatId,
        oldRoomData.name || name,
        oldRoomData.description || description,
        oldRoomData.managed || managed,
        oldRoomData.lastUpdateTime,
      ),
    );

    yield put(pushError('Failed to update room', ack.error));
  } else {
    yield put(setSagaMessage('Updated room ', name, ''));
  }
}

export function* saveChatSaga(action: SaveChat) {
  const {
    id,
    name,
    people,
    managed,
    message,
    mentioned,
    description,
    attachments,
    donorId,
    organId,
    transplantCenter,
    surgeon,
    chatroomType,
    restrictions,
  } = action;

  // Ensure takeLatest has time to handle doubleclicks
  // https://github.com/redux-saga/redux-saga/blob/master/docs/recipes/README.md#debouncing
  yield delay(50);

  const isCreating = id === undefined;

  if (!isCreating) {
    yield call(updateChatroomData, id, (description || '').trim() || 'No description for room.', name, managed);
    return;
  }

  if (!name && !donorId) {
    const errors: ChatSaveError = {};

    if (!name) {
      errors.name = 'Room name is required';
    }

    yield put(saveChatFailed(errors));

    return;
  }

  yield put(startLoading('saveChat'));

  const memberId = yield* typedSelect((state: RootState) => selectUserId(state.auth));

  const ack: CommandResult = yield sendCommandAndGetResult(-1, memberId, 'create-chatroom', [
    name,
    description,
    donorId,
    organId,
    transplantCenter,
    chatroomType,
    managed || false,
    restrictions,
  ]);

  if (!ack || !ack.result) {
    const error = ack
      ? ack.error
      : {
          error: 'Something went wrong',
        };

    yield put(
      saveChatFailed({
        nonFieldError: singleErrorMessage(error),
      }),
    );

    yield put(finishLoading('saveChat'));

    return;
  }

  const { result } = ack;
  const chatId = result.chatroom_id;

  if (chatId) {
    // Update chatlist items
    yield put(
      receiveChatroomInfo(
        chatId,
        name,
        description,
        chatroomType,
        managed,
        memberId,
        1,
        undefined,
        1,
        0,
        0,
        0,
        message
          ? {
              textContent: message,
              sentTime: isoNow(),
            }
          : undefined,
        result.last_update_time || isoNow(),
        result.last_active_time || isoNow(),
        donorId,
        organId,
        undefined,
        undefined,
        result.create_date,
      ),
    );

    // check if surgeon is different than initial state
    if (surgeon && surgeon.userId !== 0) {
      yield put(addSingleUserToChatroom(chatId, memberId, surgeon.email, surgeon.phone));
    } else {
      yield put(addUsersToChatroom(chatId, memberId, people));
    }

    if (message || (attachments && attachments.length > 0)) {
      // Send initial message
      yield call(addChatMessage, chatId, memberId, message, attachments, mentioned);
    }
  }

  yield put(resetChatEditState());
  yield put(saveChatSuccess(chatId));
  yield put(finishLoading('saveChat'));
}

export function* syncProfileInChatroomsSaga(action: SyncProfileInChatrooms): SagaIterator<void> {
  // Wait 2 seconds to ensure any new profile thumnbail has been generated and stored in s3
  yield delay(2000);

  const memberId = yield* typedSelect((state: RootState) => selectUserId(state.auth));
  const chatRooms = yield* typedSelect((state: RootState) => state.chatList.chats);
  const roomIds = keys(chatRooms);

  for (let i = 0; i < roomIds.length; i += 1) {
    const chatId = roomIds[i];
    const chat = chatRooms[`${chatId}`];

    if (chat) {
      const myMemberObject = chat.members[`${memberId}`];

      if (myMemberObject) {
        yield put(
          receiveChatroomMembers(
            chat.id,
            memberId,
            {
              [`${memberId}`]: {
                ...myMemberObject,

                profile: {
                  ...(myMemberObject || {}).profile,
                  ...action.profile,
                },
              },
            },
            [memberId],
            'extend',
          ),
        );
      }
    }
  }
}

/**
 * attemptReceiveNewMessageSaga is used whenever a new message comes in to check if
 * a room has been loaded. It will invalidate our query hook if the room isn't loaded
 * to ensure we fetch the room if needed
 */
export function* attemptReceiveNewMessageSaga(action: AttemptReceiveNewMessage): SagaIterator<void> {
  const { id, memberId, message, messageId, hint, lastMessage, lastActiveTime, sentCountModifier } = action;
  const chatRooms = yield* typedSelect((state: RootState) => state.chatList.chats);
  const userId = yield* typedSelect((state: RootState) => selectUserId(state.auth));

  // Increment the unread count for cases if this message is for a room with a case, even if
  // the room isn't loaded, and the sender isn't the current user
  if (message.caseId && userId !== message.senderId) {
    yield put(incrementCaseUnreadCount(message.caseId));
  }

  const chat = chatRooms[`${id}`];
  if (chat) {
    yield put(
      receiveNewMessage(id, memberId, message, messageId, hint, lastMessage, lastActiveTime, sentCountModifier),
    );
  } else {
    // Invalidate to query to trigger a refetch
    refetchChatList();
  }
}

export function* updateRoomIconSaga(action: UpdateRoomIcon): SagaIterator<void> {
  const { chatId, roomIcon } = action;
  const oldRoomData = yield* typedSelect((state: RootState) => state.chatList.chats[`${chatId}`] || {});

  yield put(updateRoomIconInList(chatId, roomIcon));

  const memberId = yield* typedSelect((state: RootState) => selectUserId(state.auth));
  const ack: CommandResult = (yield call(sendCommandAndGetResult, chatId, memberId, 'update-room-icon', [
    roomIcon ?? null,
  ])) as CommandResult;

  if (!ack || ack.error) {
    yield put(updateRoomIconInList(chatId, oldRoomData.roomIcon));
    yield put(pushError('Failed to update room icon', ack?.error));
  }
}
